import { uniq } from 'lodash';
import { DodCharacteristicFilterMap, DodFilters } from '@/types/DodRun';
import { DodFilterType } from '../types';
import { useMarketService } from '@/services/market.service';
import { useUser } from '@/contexts/UserContext';

/**
 * BYZ-19583 - Market characteristics (market type, channel, parent retailer, ...) are derived from the market
 * tree, which is itself scoped by the selected categories.  When categories change, some of the previously
 * selected characteristic values can stop being available - exactly like selected markets can.  This module
 * owns detecting and clearing those selections so every category mutation path behaves identically.
 *
 * NOTE: this lives outside `common/utils.ts` on purpose - `utils.ts` imports types from `DodFilterPreview`,
 * so pulling `market.service` in there would create an import cycle that breaks type inference.
 */

/** A selected market characteristic (or some of its values) that is no longer applicable. */
export type InvalidMarketCharacteristic = {
    id: string;
    displayName: string;
    /** the selected values that are no longer applicable, used for display in the confirmation popup */
    values: string[];
    /** true when the whole characteristic selection is no longer applicable and must be removed */
    removeAll: boolean;
};

/** Only category edits can invalidate market characteristics, and only when some are actually selected. */
export function isMarketCharacteristicResetRequired(filterType: DodFilterType, value: DodFilters): boolean {
    return filterType.type === 'categories' && Object.keys(value.marketCharacteristics ?? {}).length > 0;
}

/**
 * BYZ-19714 - Clearing every selected Market makes any selected Market Characteristics meaningless (they are
 * derived from the selected markets' nodes), so the same invalidation needs to fire when the Markets selection
 * itself goes to zero, not just when categories change.
 */
export function isClearAllMarketsResetRequired(
    updatedMarkets: { values: unknown[]; summedSelections: { values: unknown[] | 'all' }[] },
    marketCharacteristics: DodCharacteristicFilterMap = {}
): boolean {
    // an empty summed group (e.g. `{ values: [] }`) still counts as a "group", so checking
    // `summedSelections.length` alone would suppress the warning even when no Markets actually remain
    const hasSummedMarketValues = updatedMarkets.summedSelections.some(
        (selection) => selection.values === 'all' || (Array.isArray(selection.values) && selection.values.length > 0)
    );
    return (
        !updatedMarkets.values.length &&
        !hasSummedMarketValues &&
        Object.keys(marketCharacteristics).length > 0
    );
}

/**
 * BYZ-19714 - When every selected Market is cleared, every selected Market Characteristic is cleared along with
 * it (there's nothing left to derive them from). Unlike `getInvalidMarketCharacteristics`, there's no "still
 * applicable" set to diff against here - everything selected is being dropped - so this just describes the
 * current selections in the same `InvalidMarketCharacteristic` shape the confirmation popup already renders.
 */
export function getClearedMarketCharacteristics(
    marketCharacteristics: DodCharacteristicFilterMap = {}
): InvalidMarketCharacteristic[] {
    return Object.entries(marketCharacteristics).map(([id, filter]) => {
        const summedSelections = filter.summedSelections ?? [];
        const isAllSelected = filter.values === 'all' || summedSelections.some((ss) => ss.values === 'all');
        const selectedValues = uniq([
            ...(Array.isArray(filter.values) ? filter.values : []),
            ...summedSelections.map((ss) => (Array.isArray(ss.values) ? ss.values : [])).flat(),
        ]);

        return {
            id,
            displayName: filter.displayName ?? id,
            values: isAllSelected ? ['All'] : selectedValues,
            removeAll: true,
        };
    });
}

/**
 * Compares the selected market characteristics against the values that are still applicable for the updated
 * category selections and returns the selections that must be cleared.
 *
 * `values: 'all'` means "every value currently offered by the picker", so it can only ever be invalidated as a
 * whole - there is no stored list to prune. It is reported as `['All']` purely for display purposes.
 */
export function getInvalidMarketCharacteristics(
    marketCharacteristics: DodCharacteristicFilterMap = {},
    applicableValuesById: Record<string, string[]>,
    /**
     * BYZ-19583 - what used to be applicable for this characteristic *before* the pending category change,
     * keyed the same way as `applicableValuesById`. 'all' doesn't store concrete items (it's a dynamic
     * sentinel that always means "whatever is applicable right now"), so this is the only way to tell the
     * user what an 'all' selection is about to stop covering. Optional - when omitted, 'all' selections are
     * only reported when they become fully invalid (and are shown generically as 'All').
     */
    previousApplicableValuesById: Record<string, string[]> = {}
): InvalidMarketCharacteristic[] {
    return Object.entries(marketCharacteristics).reduce((invalid: InvalidMarketCharacteristic[], [id, filter]) => {
        const applicableValues = applicableValuesById[id];
        // characteristics that aren't driven by categories are left untouched rather than guessed at
        if (!applicableValues) return invalid;

        const summedSelections = filter.summedSelections ?? [];
        const isAllSelected = filter.values === 'all' || summedSelections.some((ss) => ss.values === 'all');
        const selectedValues = uniq([
            ...(Array.isArray(filter.values) ? filter.values : []),
            ...summedSelections.map((ss) => (Array.isArray(ss.values) ? ss.values : [])).flat(),
        ]);

        // no enabled market carries this characteristic anymore - the entire selection goes, 'all' included
        if (!applicableValues.length) {
            const previouslyApplicable = previousApplicableValuesById[id];
            invalid.push({
                id,
                displayName: filter.displayName ?? id,
                values: isAllSelected ? (previouslyApplicable?.length ? previouslyApplicable : ['All']) : selectedValues,
                removeAll: true,
            });
            return invalid;
        }

        // BYZ-19583 - 'all' is intentionally left untouched here (it keeps dynamically tracking whatever is
        // still applicable), but the user should still be told which values it no longer covers. This is
        // purely informational for the confirmation popup - `removeAll: false` with an 'all' selection is a
        // no-op for `clearInvalidMarketCharacteristics`.
        if (isAllSelected) {
            const droppedValues = (previousApplicableValuesById[id] ?? []).filter((val) => !applicableValues.includes(val));
            if (droppedValues.length) {
                invalid.push({
                    id,
                    displayName: filter.displayName ?? id,
                    values: droppedValues,
                    removeAll: false,
                });
            }
            return invalid;
        }

        const invalidValues = selectedValues.filter((val) => !applicableValues.includes(val));
        if (invalidValues.length) {
            invalid.push({
                id,
                displayName: filter.displayName ?? id,
                values: invalidValues,
                removeAll: false,
            });
        }
        return invalid;
    }, []);
}

/**
 * Removes the invalid values from the market characteristic selections, dropping a characteristic entirely once
 * nothing is left to select. Returns the original map untouched when there is nothing to clear so callers don't
 * trigger needless re-renders.
 */
export function clearInvalidMarketCharacteristics(
    marketCharacteristics: DodCharacteristicFilterMap = {},
    invalidMarketCharacteristics: InvalidMarketCharacteristic[] = []
): DodCharacteristicFilterMap {
    if (!invalidMarketCharacteristics.length) return marketCharacteristics;

    const updated = { ...marketCharacteristics };
    invalidMarketCharacteristics.forEach(({ id, values, removeAll }) => {
        const filter = updated[id];
        if (!filter) return;

        if (removeAll) {
            delete updated[id];
            return;
        }

        const remainingValues = Array.isArray(filter.values)
            ? filter.values.filter((val) => !values.includes(val))
            : filter.values;
        // summed selections that lose all of their values are dropped, otherwise an empty group would linger
        const remainingSummedSelections = (filter.summedSelections ?? [])
            .map((ss) => ({
                ...ss,
                values: Array.isArray(ss.values) ? ss.values.filter((val) => !values.includes(val)) : ss.values,
            }))
            .filter((ss) => !Array.isArray(ss.values) || ss.values.length);

        if (Array.isArray(remainingValues) && !remainingValues.length && !remainingSummedSelections.length) {
            delete updated[id];
        } else {
            updated[id] = {
                ...filter,
                values: remainingValues,
                summedSelections: remainingSummedSelections,
            };
        }
    });
    return updated;
}

/** The category state as it will look *after* the pending change. */
export type MarketCharacteristicsResetContext = {
    /** every category that will be selected (summed + non summed) */
    categories: string[];
    /** the categories that will belong to summed selections */
    summedCategories: string[];
    /** the categories that will be selected outside of summed selections */
    nonSummedCategories: string[];
    /** true while the user is building a summed market selection - affects prior approval evaluation */
    isMarketSummedMode?: boolean;
};

/**
 * BYZ-19583 - Every place that mutates categories (value picker, summed selections, preview deletions, presets,
 * clear selection) has to answer the same question before applying the change: "which of the selected market
 * characteristics are about to become invalid?". This hook owns that question so the category context is
 * assembled identically everywhere, exactly like `getEnabledMarkets` does for market selections.
 */
export function useMarketCharacteristicsReset() {
    const { getApplicableMarketCharacteristicValues } = useMarketService();
    const { company } = useUser();

    function findInvalidMarketCharacteristics(
        marketCharacteristics: DodCharacteristicFilterMap = {},
        { categories, summedCategories, nonSummedCategories, isMarketSummedMode }: MarketCharacteristicsResetContext,
        /**
         * BYZ-19583 - the category context as it looked *before* the pending change. Only used to describe
         * what an 'all' selection used to cover, so the confirmation popup can list concrete values instead
         * of the literal word "All". Skipped entirely when the caller doesn't supply it.
         */
        previousContext?: MarketCharacteristicsResetContext
    ): InvalidMarketCharacteristic[] {
        // nothing selected - skip the market scan entirely
        const characteristicIds = Object.keys(marketCharacteristics ?? {});
        if (!characteristicIds.length) return [];

        const applicableValuesById = getApplicableMarketCharacteristicValues({
            // empty strings can leak in from cleared selections and would be treated as a real category
            categories: categories.filter(Boolean),
            summedCategories: summedCategories.filter(Boolean),
            nonSummedCategories: nonSummedCategories.filter(Boolean),
            hasSummedCategories: Boolean(summedCategories.length),
            purchasedMarketKeys: company?.purchasedMarketKeys,
            isMarketSummedMode,
            // only resolve the characteristics the user actually selected
            characteristicIds,
        });

        const previousApplicableValuesById = previousContext
            ? getApplicableMarketCharacteristicValues({
                  categories: previousContext.categories.filter(Boolean),
                  summedCategories: previousContext.summedCategories.filter(Boolean),
                  nonSummedCategories: previousContext.nonSummedCategories.filter(Boolean),
                  hasSummedCategories: Boolean(previousContext.summedCategories.length),
                  purchasedMarketKeys: company?.purchasedMarketKeys,
                  isMarketSummedMode: previousContext.isMarketSummedMode,
                  characteristicIds,
              })
            : {};

        return getInvalidMarketCharacteristics(marketCharacteristics, applicableValuesById, previousApplicableValuesById);
    }

    return { findInvalidMarketCharacteristics };
}
